// Global Search Registry — Task H
// A curated, client-side registry of cards, settings, tools, and actions
// that can be discovered through the header global search.
// Each entry can navigate (path) and optionally scroll to a section (#hash).
// Roles control visibility per current user.

export const SEARCH_CATEGORIES = {
  card: { label: 'Cards', tone: 'sky' },
  setting: { label: 'Settings', tone: 'slate' },
  tool: { label: 'Tools', tone: 'violet' },
  action: { label: 'Quick Actions', tone: 'emerald' },
};

// Roles allowed for everyone (used to keep entries terse)
const ALL = ['admin', 'super_admin', 'teacher', 'student', 'finance_admin', 'parent'];
const ADMIN = ['admin', 'super_admin'];
const TEACHER = ['teacher', 'admin'];

export const GLOBAL_SEARCH_REGISTRY = [
  // ──────────────── TOOLS (AI Engine / Workspaces) ────────────────
  {
    id: 'tool-ai-content-engine',
    type: 'tool',
    name: 'AI Content Engine',
    description: 'Generate assignments, lesson plans, PPTs, assessments',
    keywords: 'ai engine generate content teaching co-pilot bosco',
    path: '/teacher-assignments',
    roles: TEACHER,
  },
  {
    id: 'tool-section-a',
    type: 'tool',
    name: 'Section A — Task Quick Generator',
    description: 'Quick task-type based content generation',
    keywords: 'section a quick generator assignment test homework',
    path: '/teacher-assignments#section-a',
    roles: TEACHER,
  },
  {
    id: 'tool-section-b',
    type: 'tool',
    name: 'Section B — Lesson Planner',
    description: 'Plan-based generator with teaching goal',
    keywords: 'section b lesson plan teaching goal planner',
    path: '/teacher-assignments#section-b',
    roles: TEACHER,
  },
  {
    id: 'tool-library',
    type: 'tool',
    name: 'Library Books',
    description: 'NCERT, International & uploaded textbooks',
    keywords: 'library books ncert international textbook',
    path: '/library',
    roles: ALL,
  },

  // ──────────────── CARDS ────────────────
  {
    id: 'card-attendance-summary',
    type: 'card',
    name: 'Attendance Summary Card',
    description: 'Today\'s attendance metrics on dashboard',
    keywords: 'attendance present absent dashboard card',
    path: '/dashboard#attendance-summary',
    roles: TEACHER,
  },
  {
    id: 'card-fee-collection',
    type: 'card',
    name: 'Fee Collection Card',
    description: 'Pending & collected fees this month',
    keywords: 'fee collection pending paid finance card',
    path: '/dashboard#fee-collection',
    roles: ['admin', 'finance_admin'],
  },
  {
    id: 'card-usage-quota',
    type: 'card',
    name: 'AI Usage Quota Card',
    description: 'Your remaining generations this month',
    keywords: 'usage quota credits remaining tier card',
    path: '/teacher-assignments#usage-quota',
    roles: TEACHER,
  },
  {
    id: 'card-recent-generations',
    type: 'card',
    name: 'Recent Generations Strip',
    description: 'Quick-reuse your last 5 generations',
    keywords: 'recent generations last 5 reuse history',
    path: '/teacher-assignments#section-a',
    roles: TEACHER,
  },

  // ──────────────── SETTINGS ────────────────
  {
    id: 'setting-profile',
    type: 'setting',
    name: 'Profile Settings',
    description: 'Edit your name, email, and avatar',
    keywords: 'profile name email avatar settings account',
    path: '/my-profile',
    roles: ALL,
  },
  {
    id: 'setting-preferences',
    type: 'setting',
    name: 'Preferences',
    description: 'Theme, language, notifications',
    keywords: 'preferences theme dark mode language notifications',
    path: '/preferences',
    roles: ALL,
  },
  {
    id: 'setting-subscription',
    type: 'setting',
    name: 'Subscription & Billing',
    description: 'Plan, invoices, upgrade options',
    keywords: 'subscription billing plan upgrade pro elite tier invoice',
    path: '/my-profile#subscription',
    roles: ['teacher'],
  },
  {
    id: 'setting-school-config',
    type: 'setting',
    name: 'School Configuration',
    description: 'Boards, classes, sections, subjects',
    keywords: 'school configuration setup admin board class section subject',
    path: '/administration',
    roles: ['admin'],
  },

  // ──────────────── ACTIONS ────────────────
  {
    id: 'action-generate',
    type: 'action',
    name: 'Generate New Content',
    description: 'Open AI Content Engine and start a new task',
    keywords: 'generate new content create assignment lesson ppt summary',
    path: '/teacher-assignments#section-a',
    roles: TEACHER,
  },
  {
    id: 'action-upgrade',
    type: 'action',
    name: 'Upgrade Plan',
    description: 'See Pro & Elite pricing options',
    keywords: 'upgrade plan pro elite pricing buy subscribe',
    event: 'ce:upgrade-required',
    eventDetail: { source: 'global-search' },
    roles: ['teacher'],
  },
  {
    id: 'action-theme-toggle',
    type: 'action',
    name: 'Toggle Dark Mode',
    description: 'Switch between light and dark theme',
    keywords: 'dark mode light theme toggle switch night',
    event: 'app:toggle-theme',
    roles: ALL,
  },
  {
    id: 'action-logout',
    type: 'action',
    name: 'Sign Out',
    description: 'Log out of your account',
    keywords: 'logout sign out exit quit',
    event: 'app:logout',
    roles: ALL,
  },
  {
    id: 'action-add-student',
    type: 'action',
    name: 'Add New Student',
    description: 'Register a new student',
    keywords: 'add student new student register enroll create',
    path: '/students?action=new',
    roles: ['admin', 'teacher'],
  },
  {
    id: 'action-add-teacher',
    type: 'action',
    name: 'Add New Teacher',
    description: 'Invite a new teacher to the school',
    keywords: 'add teacher new teacher invite faculty create',
    path: '/teachers?action=new',
    roles: ADMIN,
  },
  {
    id: 'action-mark-attendance',
    type: 'action',
    name: 'Mark Today\'s Attendance',
    description: 'Open attendance for today',
    keywords: 'mark attendance today present absent',
    path: '/attendance',
    roles: TEACHER,
  },
  {
    id: 'action-newsletter-subscribers',
    type: 'action',
    name: 'View Newsletter Subscribers',
    description: 'Super Admin — newsletter list & CSV export',
    keywords: 'newsletter subscribers list csv export email marketing',
    path: '/super-admin#newsletter',
    roles: ['super_admin'],
  },
];

/**
 * Search the registry for the given query and current user role.
 * Returns an object grouped by category type.
 */
export function searchGlobalRegistry(query, role) {
  const q = String(query || '').trim().toLowerCase();
  if (q.length < 2) return { cards: [], settings: [], tools: [], actions: [] };

  const matched = GLOBAL_SEARCH_REGISTRY.filter((item) => {
    if (!item.roles.includes(role)) return false;
    const hay = `${item.name} ${item.description} ${item.keywords || ''}`.toLowerCase();
    return hay.includes(q);
  });

  return {
    cards: matched.filter((m) => m.type === 'card'),
    settings: matched.filter((m) => m.type === 'setting'),
    tools: matched.filter((m) => m.type === 'tool'),
    actions: matched.filter((m) => m.type === 'action'),
  };
}
